import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Service to manage page-level help functionality.
 * Pages with a showHelp() method register themselves here,
 * and the breadcrumb component displays the help button when a handler is registered.
 */
@Injectable({ providedIn: 'root' })
export class HelpService {
  /** Observable that emits true when a help handler is registered */
  readonly hasHelp: Observable<boolean>;

  private _hasHelp = new BehaviorSubject<boolean>(false);
  private _helpHandler: (() => void) | null = null;

  constructor() {
    this.hasHelp = this._hasHelp.asObservable();
  }

  /**
   * Register a help handler for the current page.
   * Called automatically by BaseComponent when a page has a showHelp() method.
   */
  registerHelp(handler: () => void): void {
    this._helpHandler = handler;
    this._hasHelp.next(true);
  }

  /**
   * Unregister the help handler.
   * Called automatically by BaseComponent on destroy.
   */
  unregisterHelp(): void {
    this._helpHandler = null;
    this._hasHelp.next(false);
  }

  /**
   * Invoke the registered help handler.
   * Called by the breadcrumb component when the help button is clicked.
   */
  showHelp(): void {
    if (this._helpHandler) {
      this._helpHandler();
    }
  }
}
